import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AuthEmailForm, AuthLayout, OnEmailFormSubmitFunction } from "../AuthLayouts";
import { showErrorToast } from "../../../shared/Toasts";
import {
  fetchWithAuth,
  fetchWithoutAuth,
  FormErrorData,
  hasAuthToken,
} from "../../../../api-client/APIClient";
import { isNavigableUrl } from "../../../shared/utils/isNavigableUrl";

const SendSsoUrlScreen = () => {
  const history = useHistory();
  const [sendSsoUrlSuccess, setSendSsoUrlSuccess] = useState(false);
  const validProducts = ["unified", "agent_handler", "gateway"];
  const params = new URLSearchParams(window.location.search);
  const rawTarget = params.get("target_product");
  const targetProduct = rawTarget && validProducts.includes(rawTarget) ? rawTarget : null;

  const crossProductTarget = targetProduct && targetProduct !== "unified" ? targetProduct : null;
  const shouldCrossProductRedirect = hasAuthToken() && !!crossProductTarget;
  const crossProductFetched = useRef(false);

  // Redirect authenticated users to the target product via cross-product token exchange
  useEffect(() => {
    if (shouldCrossProductRedirect && !crossProductFetched.current) {
      crossProductFetched.current = true;
      fetchWithAuth({
        path: "account-bridge/cross-product-token/",
        method: "POST",
        body: { target_product: crossProductTarget },
        onResponse: (data: { redirect_url: string }) => {
          if (isNavigableUrl(data.redirect_url)) {
            window.location.assign(data.redirect_url);
          } else {
            history.replace("/");
          }
        },
        onError: () => {
          showErrorToast("Couldn't switch products. Please try again.");
          history.replace("/");
        },
      });
    }
  }, [shouldCrossProductRedirect, crossProductTarget]);

  if (shouldCrossProductRedirect) {
    return null;
  }

  const handleSubmit: OnEmailFormSubmitFunction = (
    data,
    setEmailErrorMessage,
    setEmailFormIsLoading,
  ) => {
    setEmailFormIsLoading(true);
    fetchWithoutAuth({
      path: "/users/sso/saml/send-url",
      method: "POST",
      body: { email: data.email, ...(targetProduct ? { target_product: targetProduct } : {}) },
      onResponse: () => {
        setEmailFormIsLoading(false);
        setSendSsoUrlSuccess(true);
      },
      onError: (err: Response | undefined) => {
        if (err) {
          err.json().then((data: FormErrorData) => {
            if (data.non_field_errors) {
              setEmailErrorMessage(data.non_field_errors[0]);
            } else if (data.email) {
              setEmailErrorMessage(data.email[0]);
            }
            showErrorToast("Failed to send SSO login url.");
            setEmailFormIsLoading(false);
          });
        } else {
          showErrorToast("Something went wrong, please check your connection and try again.");
          setEmailFormIsLoading(false);
        }
      },
    });
  };
  const title = "Sign in with SSO";
  const finePrint =
    "If you want to enable SSO for your organization, please reach out to your customer success manager or contact support@merge.dev.";
  if (!sendSsoUrlSuccess) {
    return (
      <AuthEmailForm
        title={title}
        description="If you have a Merge account, we'll email you a link to sign in with your SSO provider"
        onEmailFormSubmit={handleSubmit}
        finePrint={finePrint}
      />
    );
  }
  return (
    <AuthLayout
      title={title}
      description="If you have a Merge account and your organization uses SSO, then a SSO link has been sent to your email address"
      finePrint={finePrint}
    />
  );
};

export default SendSsoUrlScreen;
